import React, { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const pricingPlans = [
  {
    name: "Starter",
    description: "Perfect for small businesses and startups",
    price: {
      monthly: 5000,
      yearly: 40000
    },
    features: [
      "Up to 2 team members",
      "Basic project management",
      "Email support",
      "3GB cloud storage",
      "Basic analytics",
      "Mobile app access",
      "Standard templates"
    ],
    limitations: [
      "Advanced integrations",
      "Priority support",
      "Custom branding",
      "Advanced reporting"
    ],
    popular: false,
    color: "from-red-600 to-red-700"
  },
  {
    name: "Professional",
    description: "Ideal for growing businesses and teams",
    price: {
      monthly: 8000,
      yearly: 80000
    },
    features: [
      "Up to 5 team members",
      "Advanced project management",
      "Priority email & chat support",
      "5GB cloud storage",
      "Advanced analytics & reporting",
      "Mobile app access",
      "Custom templates",
      "API access",
      "Third-party integrations",
      "Custom workflows"
    ],
    limitations: [
      "White-label solution",
      "Dedicated account manager"
    ],
    popular: true,
    color: "from-red-700 to-red-900"
  },
  {
    name: "Enterprise",
    description: "For large organizations with complex needs",
    price: {
      monthly: 12000,
      yearly: 120000
    },
    features: [
      "Up to 5  team members",
      "Enterprise project management",
      "24/7 phone & chat support",
      "10GB cloud storage",
      "Advanced analytics & reporting",
      "Mobile app access",
      "Custom templates",
      "API access",
      "Advanced integrations",
      "Custom workflows",
      "White-label solution",
      "Dedicated account manager",
      "On-premise deployment",
      "Custom security features",
      "SLA guarantee"
    ],
    limitations: [],
    popular: false,
    color: "from-red-800 to-red-950"
  }
];

const additionalServices = [
  {
    title: "Custom Development",
    description: "Tailored software solutions built specifically for your business needs",
    price: "Starting at ₹1,00,000",
    features: ["Custom web applications", "Mobile app development", "API development", "System integration"]
  },
  {
    title: "Consulting Services",
    description: "Expert guidance to optimize your digital transformation journey",
    price: "₹1,000/hour",
    features: ["Technology strategy", "Process optimization", "Digital transformation", "Training & support"]
  },
  {
    title: "Managed Services",
    description: "Let us handle your IT infrastructure and maintenance",
    price: "Starting at ₹40,000/month",
    features: ["Server management", "Security monitoring", "Regular updates", "24/7 support"]
  }
];

const Product = () => {
  const [isYearly, setIsYearly] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      y: 30, 
      opacity: 0,
    },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.5, 
        ease: [0.25, 0.46, 0.45, 0.94],
      } 
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        
        .font-display {
          font-family: 'Inter', sans-serif;
          font-weight: 900;
        }
        
        .font-mono-body {
          font-family: 'JetBrains Mono', monospace;
          font-weight: 400;
        }
        
        .font-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
        }

        .pricing-card {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
        }

        .pricing-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(139, 0, 0, 0.05), transparent);
          transition: left 0.6s;
        }

        @media (hover: hover) {
          .pricing-card:hover::before {
            left: 100%;
          }

          .pricing-card:hover {
            transform: translateY(-12px);
            box-shadow: 0 25px 50px rgba(139, 0, 0, 0.2);
          }
        }

        @media (max-width: 768px) {
          .pricing-card:active {
            transform: scale(0.98);
          }
        }

        .popular-badge {
          background: linear-gradient(135deg, #8b0000 0%, #6b0000 100%);
          box-shadow: 0 4px 15px rgba(139, 0, 0, 0.3);
        }

        .cta-btn {
          transition: all 0.3s ease;
        }

        .service-card {
          transition: all 0.3s ease;
        }

        @media (hover: hover) {
          .service-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          }
        }

        /* Mobile-specific optimizations */
        @media (max-width: 640px) {
          .font-display {
            font-size: clamp(1.5rem, 8vw, 2.5rem);
          }
        }
      `}</style>

      <Navbar />
      
      <section className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div
            className="text-center mb-12 sm:mb-16 md:mb-20"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block mb-4 sm:mb-6">
              <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
                Transparent Pricing
              </span>
            </div>
            {/* <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
              Choose Your Plan
            </h1> */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display text-gray-900 mb-6 leading-tight">
                Choose Your {" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Plan
                </span>
              </h1>
            
            <div className="w-32 h-1.5 bg-gradient-to-r from-red-600 to-red-800 mx-auto mb-8 rounded-full"></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto font-mono-body mb-8 sm:mb-10 px-4 leading-relaxed">
              Flexible pricing options designed to grow with your business. No hidden fees, cancel anytime.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
              <span className={`text-sm sm:text-base font-heading transition-colors ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className={`relative w-14 sm:w-16 h-7 sm:h-8 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                  isYearly ? 'bg-gradient-to-r from-red-700 to-red-900' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full transition-transform duration-300 transform ${
                    isYearly ? 'translate-x-7 sm:translate-x-8' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-sm sm:text-base font-heading transition-colors ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
              </span>
              <span className="ml-2 px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs sm:text-sm font-heading rounded-full border border-green-300">
                Save 33%
              </span>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-20"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`pricing-card relative bg-white rounded-2xl sm:rounded-3xl shadow-xl border-2 ${
                  plan.popular ? 'border-red-700 lg:scale-105' : 'border-gray-200'
                } overflow-hidden`}
              >
                {plan.popular && (
                  <div className="popular-badge absolute top-0 right-0 px-3 sm:px-4 py-1 sm:py-2 text-white text-xs sm:text-sm font-heading rounded-bl-2xl">
                    MOST POPULAR
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  {/* Plan Header */}
                  <div className="mb-6 sm:mb-8">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4 sm:mb-6`}>
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-display text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-sm sm:text-base text-gray-600 font-mono-body leading-relaxed">{plan.description}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl sm:text-5xl md:text-6xl font-display text-gray-900">
                        ₹{(isYearly ? plan.price.yearly : plan.price.monthly).toLocaleString('en-IN')}
                      </span>
                      <span className="text-base sm:text-lg text-gray-600 font-mono-body">
                        /{isYearly ? 'year' : 'month'}
                      </span>
                    </div>
                    {isYearly && (
                      <p className="text-xs sm:text-sm text-green-600 font-mono-body mt-2">
                        Save ₹{((plan.price.monthly * 12) - plan.price.yearly).toLocaleString('en-IN')} per year
                      </p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    className={`w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-heading text-base sm:text-lg mb-6 sm:mb-8 transition-all duration-200 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-red-700 to-red-900 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Started
                  </motion.button>

                  {/* Features */}
                  <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                    <h4 className="text-xs sm:text-sm font-heading text-gray-500 uppercase tracking-wide mb-3 sm:mb-4">
                      What's Included
                    </h4>
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">
                          <CheckIcon />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 font-mono-body leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Limitations */}
                  {plan.limitations.length > 0 && (
                    <div className="space-y-3 sm:space-y-4 pt-6 sm:pt-8 border-t border-gray-200">
                      <h4 className="text-xs sm:text-sm font-heading text-gray-500 uppercase tracking-wide mb-3 sm:mb-4">
                        Not Included
                      </h4>
                      {plan.limitations.map((limitation, limitIndex) => (
                        <div key={limitIndex} className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0">
                            <XIcon />
                          </div>
                          <span className="text-sm sm:text-base text-gray-500 font-mono-body leading-relaxed">{limitation}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Partner Program Section */}
          <motion.div
            className="mb-16 sm:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-green-50 text-green-700 text-xs sm:text-sm font-heading border border-green-200">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                  </svg>
                  Earn with Us
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display text-gray-900 mb-3 sm:mb-4 px-4">
                Partner Program
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto font-mono-body px-4 leading-relaxed">
                Refer clients and earn 20% commission on every project. Join our partner network today!
              </p>
            </div>

            {/* Partner Benefits Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Program Benefits */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg sm:text-xl font-heading text-gray-900 ml-3">Program Benefits</h3>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-mono-body text-xs sm:text-sm">Commission Rate</span>
                    <span className="font-heading text-green-600 text-lg sm:text-xl">20%</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-mono-body text-xs sm:text-sm">Minimum Project Value</span>
                    <span className="font-heading text-gray-900 text-sm sm:text-base">₹50,000</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-mono-body text-xs sm:text-sm">Payment Terms</span>
                    <span className="font-heading text-gray-900 text-sm sm:text-base">30 days</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600 font-mono-body text-xs sm:text-sm">Tracking</span>
                    <span className="font-heading text-gray-900 text-sm sm:text-base">Real-time</span>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg sm:text-xl font-heading text-gray-900 ml-3">How It Works</h3>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-heading mr-3 mt-0.5 sm:mt-1 flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-heading text-gray-900 mb-1 text-sm sm:text-base">Refer a Client</h4>
                      <p className="text-gray-600 text-xs sm:text-sm font-mono-body">Connect potential clients with our team</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-heading mr-3 mt-0.5 sm:mt-1 flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-heading text-gray-900 mb-1 text-sm sm:text-base">Project Signed</h4>
                      <p className="text-gray-600 text-xs sm:text-sm font-mono-body">We close the deal and start the project</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-heading mr-3 mt-0.5 sm:mt-1 flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-heading text-gray-900 mb-1 text-sm sm:text-base">Earn Commission</h4>
                      <p className="text-gray-600 text-xs sm:text-sm font-mono-body">Receive 20% of the project value</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Example Earnings */}
            <div className="mt-6 sm:mt-8 bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg">
              <h3 className="text-xl sm:text-2xl font-heading text-gray-900 mb-4 sm:mb-6 text-center">Example Earnings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                {[
                  { project: '2,00,000', commission: '40,000' },
                  { project: '5,00,000', commission: '1,00,000' },
                  { project: '10,00,000', commission: '2,00,000' }
                ].map((earning, index) => (
                  <div key={index} className="text-center p-4 sm:p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200">
                    <div className="text-2xl sm:text-3xl font-display text-gray-900">₹{earning.project}</div>
                    <div className="text-xs sm:text-sm text-gray-600 font-mono-body mb-2 sm:mb-3">Project Value</div>
                    <div className="text-xl sm:text-2xl font-heading text-green-600">₹{earning.commission}</div>
                    <div className="text-xs text-green-600 font-mono-body">Commission</div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <motion.button
                className="cta-btn bg-green-600 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-base sm:text-lg hover:bg-green-700 shadow-lg w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Join Partner Program
              </motion.button>
              <motion.button
                className="border-2 border-green-600 text-green-600 px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-base sm:text-lg hover:bg-green-50 w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>

          {/* Additional Services */}
          <motion.div
            className="mb-16 sm:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                  Additional Services
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display text-gray-900 mb-3 sm:mb-4 px-4">
                Custom Solutions
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto font-mono-body px-4 leading-relaxed">
                Need something more specific? We offer custom solutions tailored to your unique requirements.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {additionalServices.map((service, index) => (
                <motion.div
                  key={index}
                  className="service-card bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 backdrop-blur-sm bg-opacity-90"
                  whileHover={{ y: -6 }}
                >
                  <h3 className="text-xl sm:text-2xl font-heading text-gray-900 mb-2 sm:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-3 sm:mb-4 font-mono-body text-xs sm:text-sm leading-relaxed">{service.description}</p>
                  <div className="text-2xl sm:text-3xl font-display text-red-700 mb-4 sm:mb-6">
                    {service.price}
                  </div>
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-xs sm:text-sm text-gray-700">
                        <div className="w-2 h-2 bg-red-600 rounded-full mr-3 flex-shrink-0"></div>
                        <span className="font-mono-body">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="w-full py-2.5 sm:py-3 px-4 border-2 border-red-700 text-red-700 rounded-lg hover:bg-red-50 transition-colors duration-200 font-heading text-sm sm:text-base">
                    Learn More
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* FAQ/CTA Section */}
          <motion.div
            className="text-center bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 md:p-16 shadow-2xl border border-gray-100 backdrop-blur-sm bg-opacity-90"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display text-gray-900 mb-4 sm:mb-6 px-4">
              Questions About Pricing?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-10 max-w-2xl mx-auto font-mono-body leading-relaxed px-4">
              Our team is here to help you find the right plan for your business. Get in touch with us for a personalized consultation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 px-4">
              <motion.button
                className="cta-btn bg-[#8b0000] hover:bg-[#6b0000] text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-base sm:text-lg shadow-lg w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule a Demo
              </motion.button>
              
              <motion.button
                className="border-2 border-[#8b0000] text-[#8b0000] px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-base sm:text-lg hover:bg-red-50 w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Sales
              </motion.button>
            </div>

            <div className="text-xs sm:text-sm text-gray-500 font-mono-body space-y-1 px-4">
              <p>All plans include a 14-day free trial</p>
              <p>No setup fees • Cancel anytime</p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Product;